import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, NavigationType, Route, Routes, useNavigationType } from 'react-router'

import {
  AuthorizedRoutes,
  ConnectionSetupRoutes,
  PublicRoutes,
  SettingsRoutes,
  SetupWorkspaceRoutes,
} from 'app/application/types'
import { AudienceDetails, Audiences, CreateAudience } from 'app/audiences/presentation'
import {
  Connection,
  ConnectionSetupSteps,
  Connections,
  SetupConnection,
} from 'app/connections/presentation'
import { ConnectionSettings } from 'app/connections/presentation/Setup'
import { Dashboard } from 'app/dashbaord/presentation'
import { Events } from 'app/events/presentation'
import {
  AccessSettings,
  Settings,
  UserSettings,
  UsersSettings,
  WorkspaceSelectSettings,
} from 'app/settings/presentation'
import {
  CheckInbox,
  CreateAccess,
  CreateAccessStep,
  CreateNewPassword,
  LoginPage,
  ResetPassword,
  SessionExpired,
} from 'app/user/presentation'

import { useAuthorization } from '../useAuthorization'
import { ErrorPage } from './ErrorPage'
import { Page } from './Page'

const Router = () => {
  const { authorization, setAuthorization } = useAuthorization()
  const navigationType = useNavigationType()

  useEffect(() => {
    // reset expired authorization in case of reload or browser back
    if (authorization?.state === 'expired' && navigationType === NavigationType.Pop) {
      setAuthorization(null)
    }
  }, [authorization?.state, navigationType, setAuthorization])

  return (
    <Routes>
      <Route path={PublicRoutes.CreateWorkspace} element={<CreateAccess isCreateWorkspace />}>
        <Route index element={<CreateAccessStep.SignUp />} />
        <Route path={SetupWorkspaceRoutes.Password} element={<CreateAccessStep.SetupPassword />} />
      </Route>
      <Route path={PublicRoutes.SignUp} element={<CreateAccess />}>
        <Route index element={<CreateAccessStep.SignUp />} />
        <Route path={SetupWorkspaceRoutes.Password} element={<CreateAccessStep.SetupPassword />} />
      </Route>
      <Route path={PublicRoutes.Login}>
        <Route index element={<LoginPage />} />
      </Route>
      <Route path={PublicRoutes.ResetPassword}>
        <Route index element={<ResetPassword />} />
        <Route path={SetupWorkspaceRoutes.CheckInbox} element={<CheckInbox />} />
      </Route>
      <Route path={PublicRoutes.CreateNewPassword} element={<CreateNewPassword />} />
      <Route path={PublicRoutes.SessionExpired} element={<SessionExpired />} />

      <Route
        path="/"
        element={
          authorization ? (
            // check for valid, to force a logout as this prop is new and not available in former application
            authorization.state === 'valid' ? (
              <ErrorBoundary FallbackComponent={ErrorPage}>
                <Page />
              </ErrorBoundary>
            ) : (
              <Navigate to={PublicRoutes.SessionExpired} replace />
            )
          ) : (
            <Navigate to={PublicRoutes.Login} replace />
          )
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="audiences" element={<Audiences />} />
        <Route path="audiences/create" element={<CreateAudience />} />
        <Route path="audiences/:id" element={<AudienceDetails />} />
        <Route path="events" element={<Events />} />
        <Route path={AuthorizedRoutes.Settings} element={<Settings />}>
          <Route path={SettingsRoutes.User} element={<UserSettings />} />
          <Route path="access" element={<AccessSettings />} />
          <Route
            path="users"
            element={
              authorization?.isAdmin ? <UsersSettings /> : <Navigate to={'../workspace'} replace />
            }
          />
          <Route path="workspace" element={<WorkspaceSelectSettings />} />
        </Route>
        <Route path="connections" element={<Connections />} />
        <Route path="connections/installed" element={<Connections />} />
        <Route path="connections/:name" element={<Connection />} />
        <Route path={AuthorizedRoutes.SetupConnection} element={<SetupConnection />}>
          <Route index element={<ConnectionSettings />} />
          <Route path={ConnectionSetupRoutes.SignIn} element={<ConnectionSetupSteps.SignIn />} />
          <Route
            path={ConnectionSetupRoutes.SelectWorkspaces}
            element={<ConnectionSetupSteps.SelectAccounts />}
          />
          <Route element={<ConnectionSetupSteps.StepWithConnectionAndAccounts />}>
            <Route
              path={ConnectionSetupRoutes.ConnectionVerified}
              element={<ConnectionSetupSteps.ConnectionVerified />}
            />
            <Route
              path={ConnectionSetupRoutes.Settings}
              element={<ConnectionSetupSteps.Settings />}
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
  )
}

export default Router
